import {
  FormulaCell,
  FormulaExpression,
  IFormulaExpressionEvaluator,
  TableOfColumnHistoryItem,
} from '@dpr-details/models';
import { parseAnchorOptionId } from '@dpr-details/utils';

import { FormulaExpressionCalculationService } from './formula-expression-calculation.service';

/**
 * Table-of-Columns formula calculation. There is no row-id column, so a row is
 * identified by its detail id within the card and matched to earlier cards by
 * the value of the table's anchor column.
 *
 * The template, anchor column, cells and formulas are handed to the
 * constructor: on the front end they are already loaded with the card.
 */
export class TableOfColumnFormulaExpressionCalculationService extends FormulaExpressionCalculationService<TableOfColumnHistoryItem> {
  private readonly formulasByColumnId = new Map<number, FormulaExpression>();
  private readonly currentCardAllItems: FormulaCell[];
  private readonly tableOfColumnId: number;
  private readonly anchorColumnId: number | null;

  constructor(
    evaluator: IFormulaExpressionEvaluator,
    formulas: FormulaExpression[],
    currentCardCells: FormulaCell[],
    tableOfColumnId: number,
    anchorColumnId: number | null
  ) {
    super(evaluator);
    this.currentCardAllItems = currentCardCells ?? [];
    this.tableOfColumnId = tableOfColumnId;
    this.anchorColumnId = anchorColumnId;
    for (const f of formulas ?? []) {
      if (!String.isNullOrWhiteSpace(f.expression)) {
        this.formulasByColumnId.set(f.columnId, f);
      }
    }
  }

  protected getFormula(columnId: number): FormulaExpression | null {
    return this.formulasByColumnId.get(columnId) ?? null;
  }

  protected getDetailItems(paramColumnId: number): FormulaCell[] {
    return this.currentCardAllItems.filter((d) => d.columnId === paramColumnId);
  }

  /**
   * The card's distinct rows, taken from all of its cells rather than from the
   * anchor column: a formula only needs an anchor when it self-references, so a
   * plain one must still calculate on a table with no anchor configured.
   */
  protected getAllRowIds(): (number | string)[] {
    const rowIds: (number | string)[] = [];
    const seenRowIds = new Set<string>();

    for (const cell of this.currentCardAllItems) {
      const key = String(cell.rowId);
      if (!seenRowIds.has(key)) {
        seenRowIds.add(key);
        rowIds.push(cell.rowId);
      }
    }

    return rowIds;
  }

  /**
   * The anchor cell's value for this row: the id of the list option it holds,
   * which is what identifies the same logical row on an earlier card.
   */
  protected getAnchorOptionId(rowId: number | string): number | null {
    if (this.anchorColumnId == null) {
      return null;
    }
    const anchorCell = this.currentCardAllItems.find(
      (d) =>
        d.columnId === this.anchorColumnId && String(d.rowId) === String(rowId)
    );
    return parseAnchorOptionId(anchorCell?.value);
  }

  /**
   * Scoped to this table template as well as the anchor option and column: the
   * history a caller hands over holds every table of the DPR before, and an
   * anchor option id only identifies a logical row within one template.
   */
  protected getHistoryValue(
    history: TableOfColumnHistoryItem[],
    columnId: number,
    _rowId: number | string,
    anchorOptionId: number | null
  ): string | null {
    if (
      anchorOptionId == null ||
      this.anchorColumnId == null ||
      history == null
    ) {
      return null;
    }

    const templateHistory = history.filter(
      (h) => h.tableOfColumnId === this.tableOfColumnId
    );

    // Detail (row) ids in history whose anchor cell carries this anchor
    // option — the previous cards' rows that represent the same logical row.
    const matchingDetailIds = new Set<string>();
    for (const h of templateHistory) {
      if (
        h.tableOfColumnItemId === this.anchorColumnId &&
        parseAnchorOptionId(h.value) === anchorOptionId
      ) {
        matchingDetailIds.add(String(h.dprTableOfColumnDetailId));
      }
    }

    if (matchingDetailIds.size === 0) {
      return null;
    }

    // Most recent value for the target column among those rows.
    return FormulaExpressionCalculationService.newestValue(
      templateHistory.filter(
        (h) =>
          h.tableOfColumnItemId === columnId &&
          matchingDetailIds.has(String(h.dprTableOfColumnDetailId))
      )
    );
  }
}
